import { FC, useCallback, useEffect, useRef, useState } from "react";
import styles from "./UpdateProfile.module.css"; // Importing CSS Module
import FileUpload from "../../Inputs/FileUpload/FileUpload";
import { FaUser, FaLock, FaStore } from "react-icons/fa6";
import CropControls from "../../CropControl/CropControl";
import {
  User,
  checkUsername,
  updatePassword,
} from "../../../services/UserServices";
import { useAuth } from "../../../context/AuthProvider";
import { toJpeg } from "html-to-image";
import globalStyles from "../../../styles/globalStyles.module.css";
import { useAlert } from "../../../context/AlertProvider";
import { useNavigate } from "react-router-dom";
import LoadingIcon from "../../CssIcon/Loading/LoadingIcon";
import React from "react";
import { getStripeAccountStatus } from "../../../services/StripeServices";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { usernameValidation } from "../../../utils/validations";
import CreateAccount from "../../Inputs/Stripe/CreateAccount/CreateAccount";
import Dashboard from "../../Inputs/Stripe/Dashboard/Dashboard";
import AccountLink from "../../Inputs/Stripe/AccountLink/AccountLink";
import { stripeAccountStatus } from "../../../enums/stripeAccountStatusEnum";

const UpdateProfile = () => {
  const [radioValue, setRadioValue] = useState(1); // Default selected value
  const { user } = useAuth();
  const { t } = useTranslation();

  const radios = [
    { name: t("profile.label"), value: 1, icon: <FaUser /> },
    { name: t("security"), value: 2, icon: <FaLock /> },
    { name: t("store.label"), value: 3, icon: <FaStore /> },
  ];

  const contentMap: { [key: number]: JSX.Element } = {
    1: <ProfileSection user={user} />,
    2: <SecuritySection user={user} />,
    3: <StoreSection user={user} />,
  };

  return (
    <>
      <div className={globalStyles.pageTitle}>{t("profile.update.title")}</div>
      <div className={styles.modalBodyContainer}>
        <div className={styles.toggleButtonGroup}>
          <h4>{t("settings")}</h4>
          {radios.map((radio, id) => (
            <label
              className={`${styles.label} ${
                radioValue === radio.value && styles.active
              }`}
            >
              {radio.icon}
              <input
                key={id}
                type="radio"
                name="sidebar"
                value={radio.value}
                checked={radioValue === radio.value}
                onChange={() => setRadioValue(radio.value)}
                className={styles.toggleButton}
              />
              {radio.name}
            </label>
          ))}
        </div>
        {contentMap[radioValue]}
      </div>
    </>
  );
};

interface ProfileSectionProps {
  user: User | null;
}

const ProfileSection: FC<ProfileSectionProps> = ({ user }) => {
  const [profileImage, setProfileImage] = useState<File>();
  const [profileImageSrc, setProfileImageSrc] = useState<string>(
    user?.profile_picture_url ?? ""
  );
  const [bannerImage, setBannerImage] = useState<File>();
  const [bannerImageSrc, setBannerImageSrc] = useState<string>(
    user?.banner_image_url ?? ""
  );
  const [description, seDescription] = useState<string>(
    user?.description ?? ""
  );
  const [username, setUsername] = useState<string>(user?.username ?? "");
  const { handleUpdateUser } = useAuth();
  const [isNsfw, setIsNsf] = useState<boolean>(false);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    seDescription(e.target.value);

  const { t } = useTranslation();

  const cropProfileContainerRef = useRef<HTMLDivElement>(null);
  const cropBannerContainerRef = useRef<HTMLDivElement>(null);

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      var profileFile;
      var bannerFile;

      if (cropProfileContainerRef.current && profileImage) {
        const dataProfileUrl = await toJpeg(cropProfileContainerRef.current, {
          quality: 1,
        });

        const profileBlob = await (await fetch(dataProfileUrl)).blob();
        profileFile = new File([profileBlob], "file", {
          type: "image/jpeg",
        });
      }

      if (cropBannerContainerRef.current && bannerImage) {
        const dataBannerUrl = await toJpeg(cropBannerContainerRef.current, {
          quality: 1,
        });

        const bannerBlob = await (await fetch(dataBannerUrl)).blob();
        bannerFile = new File([bannerBlob], "file", {
          type: "image/jpeg",
        });
      }

      const updatedUser: Partial<
        Omit<User, "id" | "created_at" | "updated_at" | "last_connection">
      > = {
        username: username,
        description: description,
        profile_picture_file: profileFile
          ? new Blob([profileFile], {
              type: profileFile.type,
            })
          : undefined,
        banner_image_file: bannerFile
          ? new Blob([bannerFile], {
              type: bannerFile.type,
            })
          : undefined,
      };

      await handleUpdateUser(updatedUser);
    } catch {
      showAlert(t("alert.profile.update.error"));
    } finally {
      showAlert(t("alert.profile.update.success"));
      setLoading(false);
      navigate(`/profile/${username}`);
    }
  };

  const handleProfileFileChange = (file: File) => {
    setProfileImage(file);
    const previewURL = URL.createObjectURL(file);
    setProfileImageSrc(previewURL);
  };

  const handleBannerFileChange = (file: File) => {
    setBannerImage(file);
    const previewURL = URL.createObjectURL(file);
    setBannerImageSrc(previewURL);
  };

  const debouncedCheckUsername = debounce(async (name: string) => {
    try {
      if (name.toLowerCase() !== user?.username) {
        const available = await checkUsername(name);
        setIsAvailable(available);
      }
    } catch (error) {
      setIsAvailable(false);
    }
  }, 500);

  const check = useCallback(
    (name: string) => {
      debouncedCheckUsername(name);
    },
    [debouncedCheckUsername]
  );

  useEffect(() => {
    if (username.trim() && username !== user?.username) {
      check(username);
      const isValid = usernameValidation(username);
      if (!isValid) {
        setIsValid(false);
      } else {
        setIsValid(true);
      }
    } else {
      setIsAvailable(true);
    }

    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, check]);

  return (
    <>
      {loading && <LoadingIcon />}
      <div className={styles.inputContainer}>
        <div
          className={styles.profilePictureContainer}
          ref={cropProfileContainerRef}
        >
          {profileImageSrc && <CropControls imageSrc={profileImageSrc} />}
        </div>
        <FileUpload
          onFileChange={(file: File) => handleProfileFileChange(file)}
          handleIsNsfw={(isUnsafe: boolean) => setIsNsf(isUnsafe)}
        />
        <div className={styles.bannerContainer} ref={cropBannerContainerRef}>
          {bannerImageSrc && <CropControls imageSrc={bannerImageSrc} />}
        </div>

        <FileUpload
          onFileChange={(file: File) => handleBannerFileChange(file)}
          handleIsNsfw={(isUnsafe: boolean) => setIsNsf(isUnsafe)}
        />
        <label>{t("username")}</label>
        <input
          type="text"
          className={styles.inputText}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        {isAvailable === true &&
          username.toLowerCase() !== user?.username.toLowerCase() &&
          isValid && (
            <>
              <div style={{ color: "green" }}>Username is available ✅</div>
            </>
          )}
        {isAvailable === false && username !== user?.username && (
          <p style={{ color: "red" }}>Username is taken ❌</p>
        )}
        {!isValid && (
          <p style={{ color: "red" }}>
            Username is not valid, please use alphanumric and _ .❌
          </p>
        )}
        <label>{t("description")}</label>
        <textarea
          className={styles.contentInput}
          value={description}
          onChange={handleDescriptionChange}
        />
        <button
          onClick={handleUpdateProfile}
          className={globalStyles.button}
          disabled={isNsfw || !isAvailable || !isValid}
        >
          {isNsfw ? t("nsfwDetected") : t("update")}
        </button>
      </div>
    </>
  );
};

const SecuritySection: FC<ProfileSectionProps> = ({ user }) => {
  const [email, setEmail] = useState<string>(user?.email ?? "");
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordErrorMsg, setPasswordErrorMsg] = useState<string>("");
  const [emailErrorMsg, setEmailErrorMsg] = useState<string>("");
  const { t } = useTranslation();

  const handleEmailChange = () => {
    setEmailErrorMsg("tempo");
    // API call to update email
  };

  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      setPasswordErrorMsg("Passwords do not match");
      return;
    }
    const res = updatePassword(oldPassword, newPassword);
    console.log("update user is", res);
  };

  return (
    <div className={styles.inputContainer}>
      <label>Email</label>
      <input
        type="text"
        className={styles.inputText}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      {emailErrorMsg && <div>{emailErrorMsg}</div>}
      <button onClick={handleEmailChange} className={styles.button}>
        {t("profile.update.security.email")}
      </button>
      <div>{t("profile.update.security.password")}</div>
      <label>{t("profile.update.security.oldPassword")}</label>
      <input
        type="password"
        className={styles.inputText}
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
      />
      <label>{t("profile.update.security.newPassword")}</label>
      <input
        type="password"
        className={styles.inputText}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <label>{t("profile.update.security.confirmPassword")}</label>
      <input
        type="password"
        className={styles.inputText}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      {passwordErrorMsg && <div>{passwordErrorMsg}</div>}
      <button onClick={handlePasswordChange} className={styles.button}>
        {t("update")}
      </button>
    </div>
  );
};

const StoreSection: FC<ProfileSectionProps> = ({ user }) => {
  const [accountStatus, setAccountStatus] = useState<stripeAccountStatus>(
    stripeAccountStatus.not_created
  );
  const { t } = useTranslation();

  useEffect(() => {
    const fetch = async () => {
      const status = await getStripeAccountStatus();
      setAccountStatus(status);
    };

    fetch();
  }, []);

  return (
    <div className={styles.inputContainer}>
      <p>{t("stripe.collaborationMsg")}</p>
      {accountStatus === stripeAccountStatus.not_created && <CreateAccount />}
      {accountStatus === stripeAccountStatus.pending && <AccountLink />}
      {accountStatus === stripeAccountStatus.completed && <Dashboard />}
    </div>
  );
};
export default UpdateProfile;
